"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";

import { cn } from "@/shared/lib/utils/cn";
import { onWorkflowCanvasVisible } from "@/shared/builder-session";
import { RoutePendingOverlay } from "@/shared/ui/route-pending";
import { AppSidebar } from "./AppSidebar";

export function AppShell({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const collapsedRef = useRef(collapsed);

  useEffect(() => {
    collapsedRef.current = collapsed;
  }, [collapsed]);

  useEffect(() => {
    return onWorkflowCanvasVisible((open) => {
      // Auto-collapse over the canvas; never auto-expand — user opens it.
      if (!open || collapsedRef.current) return;
      setCollapsed(true);
    });
  }, []);

  return (
    <div className="relative h-screen overflow-hidden bg-[var(--page)]">
      {/* Content keeps sidebar clearance; page atmospheres can still paint full-bleed under the floater */}
      <div
        className={cn(
          "absolute inset-0 flex min-h-0 min-w-0 flex-col overflow-hidden transition-[padding] duration-200",
          collapsed ? "pl-[5rem]" : "pl-[18rem]",
        )}
      >
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          {children}
          <RoutePendingOverlay />
        </div>
      </div>

      <div className="pointer-events-none absolute inset-y-3 left-3 z-40">
        <div className="pointer-events-auto h-full">
          <AppSidebar
            collapsed={collapsed}
            onToggle={() => {
              setCollapsed((value) => !value);
            }}
          />
        </div>
      </div>
    </div>
  );
}
